import { SUPPORT_EMAIL } from '@/lib/constants'
import { createFileRoute } from '@tanstack/react-router'
import type { LegalPageContext } from './route'

export const Route = createFileRoute('/_legal_pages/terms')({
  component: Terms,
  beforeLoad: (): LegalPageContext => ({
    title: 'TERMS AND CONDITIONS',
    lastUpdated: 'December 13, 2024',
  }),
})

function Terms() {
  return (
    <>
      <p className="mb-4">
        <strong>Introduction</strong>
        <br />
        Supplyco makes available to Customer an external data platform as
        further defined in these Terms & Conditions (the &quot;
        <strong>Platform</strong>
        &quot;) capable of exploring thousands of
        internal or external data sources in the context of building
        predictive models. Any Order Form or other purchasing document
        mutually agreed by Supplyco and Customer, in writing, will be
        governed by these Terms and Conditions.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        1. DEFINITIONS
      </h2>
      <div className="ml-4 space-y-3 mb-6">
        <p>
          &quot;
          <strong>Agreement</strong>
          &quot; means these Terms and
          Conditions, the Order Form attached hereto, together with any
          other exhibit or addendum attached hereto.
        </p>
        <p>
          &quot;
          <strong>API</strong>
          &quot; means the application
          programming interface that facilitates the transfer of data
          based on individual queries between Supplyco and Customer.
        </p>
        <p>
          &quot;
          <strong>Consume</strong>
          &quot; or &quot;
          <strong>Consumption</strong>
          &quot; means the extraction of
          Enriched Data from the Platform by the Customer, including
          without limitation by means of download, API, CRM Integration,
          extraction from the Scout application, or by any other method
          then made available to Customer through the Platform.
        </p>
        <p>
          &quot;
          <strong>CRM Integration</strong>
          &quot; allows Platform
          Data to be injected directly from the Platform to
          Customer&apos;s own CRM tool, to allow enrichments of
          Customer&apos;s CRM.
        </p>
        <p>
          &quot;
          <strong>Customer Data</strong>
          &quot; means all data record
          uploaded to or stored on the Platform by the Customer, except
          for data or other materials that are publicly available.
        </p>
        <p>
          &quot;
          <strong>Data Gallery</strong>
          &quot; means the
          Company&apos;s proprietary or licensed data gallery comprising
          its many online sources and own databases.
        </p>
        <p>
          &quot;
          <strong>Documentation</strong>
          &quot; means the
          documentation for the Platform, Platform Data, or Services
          produced by Company and delivered or made available by the
          Company to the Customer.
        </p>
        <p>
          &quot;
          <strong>Effective Date</strong>
          &quot; means the date of
          execution of this Agreement, as identified in the Order Form.
        </p>
        <p>
          &quot;
          <strong>Enrichment</strong>
          &quot; or &quot;
          <strong>Enriched</strong>
          &quot; means a match between Customer
          Data and Platform Data, including without limitation by: (i)
          adding new Signals to a Record; (ii) generating a set of Signals
          based on the values of data in each Record; or (iii) changing
          the value of existing data elements within a Record. A Record is
          considered Enriched if there is any, including partial matching
          between such Record and Signals within the Platform.
        </p>
        <p>
          &quot;
          <strong>Scout tool</strong>
          &quot; means the application
          within the Platform which enables Customers to view and extract
          relevant information about client prospects from the Platform
          Data, with specific insights for customers from manufacturing
          industries.
        </p>
        <p>
          &quot;
          <strong>The Signal Generating App</strong>
          &quot; means the
          capability within the Platform which enables Customer to query
          or generate Signals from Platform Data without the need for any
          Customer Data (no matching required). The dataset generated can
          be consumed, or used in Signal Studio or ML engine in lieu of
          Customer Data.
        </p>
        <p>
          &quot;
          <strong>Intellectual Property Rights</strong>
          &quot; means
          all intellectual property rights wherever in the world, whether
          registrable or un-registrable, registered or unregistered,
          including any application or right of application for such
          rights, including copyrights and related rights, database
          rights, confidential information, trade secrets, know-how,
          business names, trade names, trademarks, service marks, passing
          off rights, unfair competition rights, patents, petty patents,
          utility models.
        </p>
        <p>
          &quot;
          <strong>ML Engine</strong>
          &quot; means the capability
          within the Platform that allows for building, testing, training,
          deploying and monitoring predictive models.
        </p>
        <p>
          &quot;
          <strong>Net New Leads</strong>
          &quot;- identification of,
          and provision of data and information about new client prospect
          within Customer&apos;s TAM which were not disclosed in
          Customer&apos;s CRM file provided by Customer to Supplyco upon
          initiation of the project.
        </p>
        <p>
          &quot;
          <strong>Order Form</strong>
          &quot; means the ordering
          document that is entered into between Customer and Supplyco,
          including any addenda thereto and which outline, among other,
          the scope, the term, fee and payment terms.
        </p>
        <p>
          &quot;
          <strong>Platform</strong>
          &quot; means Company&apos;s
          external data platform consisting of a wide range of
          applications and capabilities, including its ML Engine, Signal
          Studio the Generate Application , CRM Integration tool, TAM
          tool, Scout Application, and any newly available capability
          which may be provided to the Customer from time to time. For
          avoidance of doubt, Company may license use of the Platform in
          its entirety or limit to specific applications and features
          within the Platform as outlined in each Customer Order Form.
        </p>
        <p>
          &quot;
          <strong>Platform Data</strong>
          &quot; means the data
          Signals made available to Customer in any way or form, whether
          through the Platform, direct delivery, API, or any other means,
          including but not limited to ML Engine, CRM Integration API, TAM
          Tool, Scout Application, Signal Studio or Starters.
        </p>
        <p>
          &quot;
          <strong>Platform Logs</strong>
          &quot; means the usage
          information retrieved by the Company when a Customer uses the
          Platform, including customer name, customer details, billing
          information, contact details of customer employees and
          representatives who are using the Platform, behavioral and
          technical information such as clicks, scrolls, focuses (i.e.
          zoom), errors, bugs, and navigation patterns. Platform Logs are
          Intellectual Property of Supplyco.
        </p>
        <p>
          &quot;
          <strong>Query</strong>
          &quot; means the request of Customer
          to Enrich a Record through the Platform.
        </p>
        <p>
          &quot;
          <strong>Record</strong>
          &quot; means the set of all the
          data elements related to a single entity, submitted by the
          Customer.
        </p>
        <p>
          &quot;
          <strong>Record Consumption Limit</strong>
          &quot; means the
          maximum number of Records Consumed by Customer per Term as
          agreed in the Order Form. Each time a Record is consumed will
          count toward the Record Consumption Limit; This occurs
          regardless of the method of consumption and whether this same
          Record has previously been Consumed.
        </p>
        <p>
          &quot;
          <strong>Services</strong>
          &quot; means any services that
          the Company provides to the Customer, as outlined in the Order
          Form attached to this Agreement, including providing support,
          improving the services, and including, identifying,
          investigating, or resolving technical or security issues if any.
        </p>
        <p>
          &quot;
          <strong>SDK</strong>
          &quot; means software development kit
          that may be provided by Company.
        </p>
        <p>
          &quot;
          <strong>Signal</strong>
          &quot; each data element within the
          Platform Data.
        </p>
        <p>
          &quot;
          <strong>Signal Studio</strong>
          &quot; means the capability
          within the Platform which enables Customer to find, create and
          integrate the relevant Signals, which Customer seeks for Record
          Enrichment.
        </p>
        <p>
          &quot;
          <strong>TAM Tool/ TAM Report</strong>
          &quot;- A reporting
          tool extracted from Platform Data which identifies
          Customer&apos;s TAM (Total Addressable Market) within a specific
          geography. Report may be provided to customer via offline report
          or via Platform access as applicable per Customer&apos;s Order
          Form.
        </p>
        <p>
          &quot;
          <strong>Term</strong>
          &quot; the term as defined in the
          Order Form.
        </p>
      </div>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        2. LICENSE
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          <strong>License Granted.</strong>
          {' '}
          Subject to the terms of this
          Agreement and as specified in the applicable Order Form, Company
          grants to Customer during the Term, a limited, revocable,
          non-exclusive, non-transferable license to: (a) use the Platform
          (or any specific application within the Platform as specified in
          the applicable Order Form) and Platform Data for its internal
          business data science purposes and (b) access the Platform via
          API or SDK provided by Company to Customer. Such license is
          solely for the purposes of generating internal predictive
          models, deriving insights or conducting data analysis for
          Customer&apos;s internal use. This license does not transfer to
          Customer any ownership rights to the Platform, the API or
          Platform Data or other Intellectual Property Rights embodied or
          used in connection with the Platform, API or Platform Data. All
          right, title, and interest in and to the Platform, including the
          user interface, SDK, API, the Platform Data, and the
          Intellectual Property Rights associated therewith, are and will
          remain with Company and its licensors. Nothing in this Agreement
          may be interpreted as transferring or conferring on Customer any
          right to use the Intellectual Property Rights of the Company. No
          rights or subscription licenses are granted except as expressly
          set forth herein.
        </li>
        <li className="pl-2">
          The Platform will be available in accordance with the Service
          Levels set forth in Exhibit A and the Support Terms set forth in
          Exhibit B.
        </li>
        <li className="pl-2">
          <strong>Restrictions on Use.</strong>
          <ol className="list-decimal ml-6 mt-2 space-y-2">
            <li className="pl-2">
              Customer may not sublicense, transfer, sell, lease,
              distribute or share, syndicate, the Platform or create
              derivative works of the API, the Platform, or otherwise
              provide access to the API, SDK, Platform user interface or
              to any Platform Data to any third party not contemplated by
              this Agreement, for any purpose, without Company&apos;s
              prior written consent.
            </li>
            <li className="pl-2">
              This is an internal use license only, Customer may not
              resell, sublicense, distribute, transfer, or share the
              Platform Data and any predictive models or derived insights
              or data analysis obtained by use of the Platform.
            </li>
            <li className="pl-2">
              Customer may not translate, disassemble, decompile or
              reverse-engineer in whole or in part the Platform, API or
              Platform Data or seek to reconstruct or discover source
              code, object code, HTML, Javascript, other code, know-how or
              algorithms associated with the API and Platform Data.
            </li>
            <li className="pl-2">
              Customer may not remove, disable, or otherwise create or
              implement any workaround to, any security features of the
              Platform or API.
            </li>
            <li className="pl-2">
              The Customer will not use the Platform or the API in any way
              that causes, or may cause, damage to the Platform or
              impairment of the availability or accessibility of the
              Platform or to any third party.
            </li>
            <li className="pl-2">
              The Customer shall not use the Platform or Platform Data in
              any way that is unlawful, illegal, discriminating, abusive,
              fraudulent or harmful, and in non-compliance with any
              applicable laws, including without limitation any applicable
              privacy regulation nor shall it assist or allow others to do
              so.
            </li>
            <li className="pl-2">
              Customer agrees that the Platform, API, or Platform Data
              will not be used to send unsolicited emails, false
              advertising, place unsolicited calls or any other action by
              or on behalf of Customer that may be considered spam or
              otherwise in violation of CAN-SPAM or related regulations.
              For clarity, this does not prohibit Customer from engaging
              in communications that are otherwise permissible.
            </li>
            <li className="pl-2">
              Customer shall not use the Platform Data to sell, promote,
              or solicit directory listings, business directories, or
              directory advertising in any print or digital format.
              Representing the Platform Data as available to appear (or
              implying it appears) in any publication or database is
              prohibited. The Platform Data may not be used to help
              compile or enhance a database, mailing list, or directory to
              be published, posted online, or otherwise revealed, sold, or
              transferred to any 3rd party.
            </li>
            <li className="pl-2">
              It is Customer&apos;s sole responsibility to ensure that its
              use of the Platform Data is in full compliance with all
              applicable federal, state, and local laws, statutes, and
              regulations for privacy, direct marketing, email,
              telemarketing, fax, and the use of personal information.
              Customer is advised that some contacts listed in the Data
              may be physically located outside the U.S. and may be
              subject to additional data privacy laws or restrictions set
              by other countries.
            </li>
            <li className="pl-2">
              Companies and contacts listed in Platform Data have not
              &quot;opted in&quot; to be contacted by Customer nor have
              undergone &quot;opt out&quot; processing. Customer will
              promptly honor all requests by recipients not to be
              contacted again. Customer assumes responsibility for any
              complaints from recipients regarding communication in any
              form from Customer or its representatives. Customer will not
              disclose to any recipient that their personal information
              was obtained from Supplyco, refer to Supplyco by name or
              inference, nor suggest in any way that the recipient contact
              Supplyco to remove their information from Supplyco&apos;s
              database.
            </li>
            <li className="pl-2">
              While the use of the Platform Data is at its own discretion,
              Customer will not knowingly violate federal, state, or local
              laws, nor use the Platform Data to promote products,
              services, or content that are illegal, illicit, harmful,
              hateful, inappropriate, or unsuitable for the intended
              audience. Customer is advised that some contacts listed in
              the Platform Data may be minors.
            </li>
            <li className="pl-2">
              Supplyco cannot guarantee that the use of the Platform Data
              will meet Customer&apos;s requirements or achieve a certain
              level of success. No refunds or credits are provided should
              Licensee&apos;s use of the Platform Data not produce the
              desired results. Supplyco shall not be liable for special,
              punitive, incidental, indirect, or consequential damages,
              loss of value, loss of production, loss of financial
              advantage, loss of profit or business opportunities
              resulting from the use of the Platform Data.
            </li>
          </ol>
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        3. CUSTOMER RESPONSIBILITIES
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          Customer shall be responsible for maintaining the security of
          the Customer account, passwords (including but not limited to
          administrative and user passwords) and files, and for all uses
          of Customer account with or without Customer&apos;s knowledge or
          consent.
        </li>
        <li className="pl-2">
          Customer hereby represents and warrants that any and all
          Customer Data, is obtained by Customer in compliance with
          applicable law, and Customer has the legal right to disclose all
          Customer Data to the Company in such manner that would allow
          Company to enrich Customer&apos;s Data pursuant to the terms of
          this Agreement, through the use of Company&apos;s partners&apos;
          and affiliates&apos; data sources and has obtained all approvals
          required for such disclosures from the applicable information
          owners. Customer shall place a privacy policy on its website or
          applications and shall disclose to its end-users the disclosure
          of such information to the Company.
        </li>
        <li className="pl-2">
          Customer hereby agrees that Customer Data shall not, at any
          time, include: (i) any patient, medical, or other
          &quot;protected health information&quot; as defined by and
          subject to HIPAA or any similar federal, state or local laws and
          regulations; (ii) any information provided on a government
          issued ID, including but not limited to passport or
          driver&apos;s license, social security number, or other
          identification number; (iii) Unique electronic identifiers or
          electronic signatures; or (iv) any bank account or payment card
          data.
        </li>
        <li className="pl-2">
          Certain parts of the Platform and Service may allow Customer to
          integrate with other third-party tools for which Customer hold
          accounts, such as connectivity to Customer&apos;s CRM tool. If
          Customer chooses to use these parts of the Service, Customer
          grants Company all licenses and rights necessary for Company to
          provide the Service to the Customer, including a license to
          extract data from and upload data to your third-party account,
          and to share data extracted from your third-party account with
          third parties as per Section 5.2 of this Agreement and solely
          for the purposes of providing the services to Customer. Any data
          obtained from any third party tool shall be deemed Customer
          Data.
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        4. PAYMENT TERMS
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          <strong>Payment.</strong>
          {' '}
          In consideration of license granted
          herein, Customer shall pay the fees identified in the Order
          Form. Unless otherwise provided in the Order Form, Payments
          shall be made upon execution of the Order Form and for the
          entire Initial Term. Any other fees will be due within thirty
          (30) days from the date of Company&apos;s invoice and according
          to the payment details provided therein. If any fees are past
          due, Company may assess interest on the past due amount at the
          rate of one and one half percent (1.5%) per month or, if lower,
          the highest rate permitted under applicable law. Interest will
          accrue from the due date of such fees. In addition to and not in
          lieu of any other remedy, Company may suspend Customer&apos;s
          access to the Company API, the Platform and Platform Data if
          Customer fails to pay any amount when due pursuant to this
          Section. All amounts and fees stated or referred to in this
          Agreement, once incurred are non-cancellable and non-refundable.
          Customer&apos;s obligation to pay invoiced amounts is absolute
          and unconditional and not subject to any offset, suspension due
          to late payments defense or counterclaim. If delivery of the
          Services is interrupted due to non-payment or non-compliance,
          Customer shall nonetheless be responsible for any fees as set
          forth in this Agreement. Notwithstanding the foregoing, If
          Customer fails to timely pay an invoice as set forth in this
          Section or in any applicable Order Form (1) on two or more
          occasions ; or (2) for longer than 60 days, Company may, in
          addition to any other remedies that it may have and upon written
          notice to Customer, modify the payment terms to require
          pre-payment of any or all fees payable under any Order Forms
          then outstanding or entered into in the future, or require other
          assurances to secure Customer&apos;s payment obligations
          hereunder.
        </li>
        <li className="pl-2">
          <strong>Taxes.</strong>
          {' '}
          The fees identified in the Order Form
          are exclusive of all sales, use, value add or other taxes.
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        5. CONFIDENTIALITY; PROPRIETARY RIGHTS
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          <strong>Mutual Obligations.</strong>
          {' '}
          Each party (the
          &quot;Receiving Party&quot;) understands that the other party
          (the &quot;Disclosing Party&quot;) has disclosed or may disclose
          business, technical or financial information relating to the
          Disclosing Party&apos;s business (hereinafter referred to as
          &quot;Proprietary Information&quot; of the Disclosing Party).
          Proprietary Information of Company includes non-public
          information regarding features, functionality and performance of
          the Platform, API, Platform Data and Data Gallery, names of data
          partners, or other data sources, data provided from
          Company&apos;s data partners and other enrichment sources
          computer programs in any form, documentation, installation
          programs, software code, actual products, prototypes, samples,
          designs, schematics, configurations, specifications, techniques.
          Proprietary Information of Customer includes Customer Data.
          Proprietary Information of the Customer does not include any
          Platform Logs that do not contain or relate to data provided by
          Customer. &quot;Proprietary Information&quot; means collectively
          Proprietary Information of Company and Customer. The Receiving
          Party agrees: (i) to take reasonable precautions to protect such
          Proprietary Information, and (ii) not to use (except in
          performance of the Services, if any, or as otherwise permitted
          herein) or divulge to any third person any such Proprietary
          Information, (iii) to disclose Proprietary Information solely to
          its employees or consultants who have a need to know the
          Proprietary Information and have undertaken similar
          confidentiality obligations towards the Receiving Party. The
          Disclosing Party agrees that the foregoing shall not apply with
          respect to any information (except with regard to any personal
          identifiable information to the extent it is exchanged between
          the Parties) after five (5) years following the disclosure (even
          if after Termination of this Agreement) thereof or any
          information that the Receiving Party can document (a) is or
          becomes generally available to the public, (b) was in its
          possession or known to it prior to receipt from the
          DisclosingParty, (c) was rightfully disclosed to it without
          restriction by a third party, (d) was independently developed
          without use of any Proprietary Information of the Disclosing
          Party or (e) is required to be disclosed by law, except that
          Recipient will disclose only such information as is legally
          required and will use reasonable efforts to obtain confidential
          treatment for any Confidential Information that is so disclosed.
        </li>
        <li className="pl-2">
          <strong>Customer Data.</strong>
          {' '}
          As between the Parties, Customer
          shall own all right, title and interest in and to the Customer
          Data. Unless otherwise provided herein, during and after the
          Term, Company shall have no right to share the Customer Data
          with any third-party without Customer&apos;s prior written
          consent. Customer hereby grants to Company a non-exclusive,
          royalty free right to use, copy, store, transmit, modify, create
          derivative works of, and display the Customer Data solely to the
          extent necessary to meet its obligations under this Agreement.
          Notwithstanding the foregoing, Company may share Customer Data
          with Company&apos;s cloud services provider ( which shall be one
          of AWS/ AZURE/ Google Cloud), or as required in order to enrich
          Customer&apos;s Data, through the use of Company&apos;s data
          sources, provided however that any such use shall always be
          subject to Company&apos;s agreements with such sources and
          include appropriate confidentiality provisions and to
          Company&apos;s Privacy Policy available on or through
          {' '}
          <a
            href="https://www.supplyco.ai/privacy"
            className="text-primary hover:underline"
          >
            www.supplyco.ai/privacy
          </a>
          .
        </li>
        <li className="pl-2">
          <strong>Company Intellectual Property.</strong>
          {' '}
          Company shall
          own and retain all right, title and interest in and to: (a) the
          Platform, API, Platform Data, Data Gallery, Platform Logs (b)
          all Documentation, improvements, enhancements or modifications
          thereto, (c) any software, applications, inventions or other
          technology developed in connection with the Services or support,
          provided to Customer and (d) all Intellectual Property rights
          related to any of the foregoing. Customer will not take any
          action inconsistent with the Intellectual Property Rights of the
          Company.
        </li>
        <li className="pl-2">
          <strong>Publicity.</strong>
          {' '}
          Except as expressly authorized in an
          applicable Order Form, neither party will make any use of the
          other party&apos;s logos and trademarks in any manner, including
          any manner that dilutes, tarnishes or undermines the value of
          the other party&apos;s marks. Notwithstanding the foregoing,
          Company may mention Customer as a customer of the Company and
          use its name and logo on a dedicated area on Company&apos;s
          website and in marketing materials. Any press release or other
          publicity announcing or referring to this Agreement or the
          relationship between the parties, shall be subject to the prior
          written approval of the other party.
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        6. MUTUAL REPRESENTATIONS AND WARRANTIES
      </h2>
      <p className="mb-4">
        Each Party represents and warrants to the other Party that: (i)
        such Party has the right, power and authority to enter into this
        Agreement, to grant the licenses granted hereunder and to perform
        the acts required of it hereunder; (ii) such Party is duly
        organized and in good standing as a corporation or other entity
        under the laws of the jurisdiction of its incorporation; (iii) the
        execution of this Agreement by its representative has been duly
        authorized by all necessary corporate or organizational action of
        such party; (iv) the execution of this Agreement by such Party,
        and the performance by such Party of its obligations and duties
        hereunder, do not violate any agreement to which such Party is a
        party or by which it is otherwise bound or any applicable law; (v)
        when executed and delivered by such Party, this Agreement will
        constitute the legal, valid and binding obligation of such Party,
        enforceable against such Party in accordance with its terms; and
        (vi) such Party acknowledges that the other Party makes no
        representations, warranties or agreements related to the subject
        matter hereof which are not expressly provided for in this
        Agreement.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        7. WARRANTY AND DISCLAIMER
      </h2>
      <p className="mb-4">
        Company shall use reasonable efforts consistent with prevailing
        industry standards to maintain the Services in a manner which
        minimizes errors and interruptions in the Services and shall
        perform the Services in a professional and workmanlike manner.
        Services may be temporarily unavailable for scheduled maintenance
        or for unscheduled emergency maintenance, either by Company or by
        third-party providers, or because of other causes beyond
        Company&apos;s reasonable control. Company does not warrant that
        the Services will be uninterrupted or error free; nor does it make
        any warranty as to the results that may be obtained from use of
        the Services. EXCEPT AS EXPRESSLY SET FORTH IN THIS SECTION, THE
        PLATFORM, PLATFORM DATA, AND SERVICES ARE PROVIDED &quot;AS
        IS&quot; AND COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED,
        INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF
        MERCHANTABILITY, PROFITABILITY, FITNESS FOR A PARTICULAR PURPOSE,
        AND IMPLIED WARRANTIES ARISING FROM DATA ACCURACY, COURSE OF
        DEALING OR COURSE OF PERFORMANCE AND NON-INFRINGEMENT.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        8. LIMITATION OF LIABILITY
      </h2>
      <p className="mb-4">
        COMPANY AND ITS SUPPLIERS (INCLUDING BUT NOT LIMITED TO ALL
        EQUIPMENT, DATA AND TECHNOLOGY SUPPLIERS), OFFICERS, AFFILIATES,
        REPRESENTATIVES, CONTRACTORS AND EMPLOYEES SHALL NOT BE
        RESPONSIBLE OR LIABLE WITH RESPECT TO ANY SUBJECT MATTER OF THIS
        AGREEMENT AND THE SERVICES OR TERMS AND CONDITIONS RELATED THERETO
        UNDER ANY CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY OR OTHER
        THEORY: (A) FOR ERROR OR INTERRUPTION OF USE OR FOR LOSS OR
        INACCURACY OR CORRUPTION OF DATA OR COST OF PROCUREMENT OF
        SUBSTITUTE GOODS, SERVICES OR TECHNOLOGY OR LOSS OF BUSINESS OR
        LOSS OF PROFIT; (B) FOR ANY INDIRECT, EXEMPLARY, INCIDENTAL,
        SPECIAL OR CONSEQUENTIAL DAMAGES; (C) FOR ANY MATTER BEYOND
        COMPANY&apos;S REASONABLE CONTROL; OR (D) FOR ANY DAMAGE CAUSED BY
        (OR RELATED TO) ANY ACT OR OMISSION OF ANY THIRD PARTY (E) FOR ANY
        AMOUNTS THAT, TOGETHER WITH AMOUNTS ASSOCIATED WITH ALL OTHER
        CLAIMS, EXCEED THE FEES PAID BY CUSTOMER TO COMPANY FOR THE
        SERVICES UNDER THIS AGREEMENT IN THE 12 MONTHS PRIOR TO THE ACT
        THAT GAVE RISE TO THE LIABILITY, IN EACH CASE, WHETHER OR NOT
        COMPANY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        9. INDEMNIFICATION
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          Customer shall indemnify, defend and hold harmless, at
          Customer&apos;s sole expense, Company, and its affiliates,
          directors, officers, employees, agents, and shareholders,
          successor and assigns from and against any and all claims,
          actions, demands, liabilities, losses, damages, judgments,
          settlements, costs and expenses (including reasonable
          attorneys&apos; fees) (&quot;Liabilities&quot;) incurred by
          Company resulting from third party claims that arise or result
          from, or are alleged to arise or result from: (i)
          Customer&apos;s or its Subcontractor&apos;s (as defined in
          Section 2.3.1) use of Services, Platform or Platform Data
          violated applicable laws and regulations or the license
          restrictions in this Agreement; or (ii) that Customer Data
          provided to the Company in any way pursuant to this Agreement
          was provided in a manner that infringes any applicable privacy
          law, Company&apos;s or Customer&apos;s Privacy Policy, or that
          the required permissions were not duly obtained from the
          individual owners of the information. In addition, the use of
          the Platform, the Platform Data and the services by Customer
          shall be at its own risk and Customer will indemnify, defend and
          hold harmless Company and its third-party suppliers from and
          against all Liabilities that arise with respect to any use of
          the Platform, the Platform Data and the services by Customer.
        </li>
        <li className="pl-2">
          Company shall indemnify, defend and hold harmless, at
          Company&apos;s sole expense, Customer, and its, directors,
          officers, employees, agents, and shareholders from and against
          any and all Liabilities incurred by Customer arising directly
          from third party claims Company&apos;s Services infringes any
          patent or copyright of any third-party with respect to licenses
          granted pursuant to this Agreement. Company will have no
          obligations under this Section 9.2 if the relevant claim or
          allegation (i) arose from Customer&apos;s negligence or willful
          misconduct, (ii) was due Customer&apos;s use of the Service
          outside of the terms of this Agreement, in violation of
          applicable law, or use of the Platform Data or Services in
          combination with any other material or services not provided by
          Company. In the event that any Company&apos;s Service, the
          Platform, API, or Platform Data becomes subject to a claim that
          is based on infringement of any third party intellectual
          property rights or privacy rights, Company shall at its option
          either (i) modify the Service, the Platform, API, or Platform
          Data or any part thereof as applicable, so as to avoid any
          alleged infringement or violation; (ii) obtain any rights
          required to avoid such alleged infringement or violation; or
          (iii) terminate this Agreement with respect to such Service, the
          Platform, API, or Platform Data and refund to Customer any
          prepaid fees applicable to the remainder of the then-current
          Term up to the lower of: (1) the unused portion of any unused
          Record Consumption Limits or (2) the pro-rata time remaining
          until the end of the Term. This section 9.2 sets forth
          Customer&apos;s sole remedies and company&apos;s sole liability
          and obligation for any actual, threatened, or alleged claims
          that the services and company materials or any subject matter of
          this agreement infringes, misappropriates, or otherwise violates
          any intellectual property rights of any third party.
        </li>
        <li className="pl-2">
          A party seeking to be indemnified (the &quot;Indemnified
          Party&quot;) shall provide the other (the &quot;Indemnifying
          Party&quot;) prompt notice immediately after it becomes aware of
          a claim or any other matter it believes is indemnifiable
          hereunder (hereinafter &quot;Action&quot;). Such notice shall
          (i) provide the basis on which indemnification is being asserted
          and (ii) be accompanied by copies of all relevant pleadings,
          demands, and anything else related to the Action. The
          Indemnifying Party will then assume control of the Action,
          including selection of counsel. In the event that the
          Indemnifying Party fails to assume control of the defense, the
          Indemnified Party may employ counsel, and the Indemnifying Party
          will pay the reasonable legal fees. No settlement or payment in
          connection with any such settlement may be made without the
          prior consent of the Indemnifying Party, which consent will not
          be unreasonably withheld.
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        10. AUDIT
      </h2>
      <p className="mb-4">
        Customer shall maintain current and accurate records regarding its
        use of the Platform and the Platform Data. If Company reasonable
        believes that Customer has breached this Agreement, and upon 5
        business days advance written notice, including email, Customer
        shall give Company (a) on-site or remote access to Customer&apos;s
        applicable systems and records and (b) make available during
        Customer&apos;s normal business hours, Customer&apos;s
        knowledgeable personnel who are able to respond to questions or
        inquiries regarding such records or systems, for the purpose of
        auditing and verifying that Platform Data was used as permitted
        pursuant to this Agreement and that all payment obligations had
        been fulfilled. To the extent the audit shows a discrepancy of use
        or payment higher than 5% of the fee or proves material breach of
        license restrictions, in addition to any other remedy available to
        Company, Customer will bear all costs of such Audit.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        11. RELATIONSHIP OF THE PARTIES
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          <strong>Non-exclusive.</strong>
          {' '}
          Company&apos;s provision of the
          Services to Customer is non-exclusive Nothing in the Agreement
          prevents Company from providing the Services to any other third
          party.
        </li>
        <li className="pl-2">
          <strong>Contractor.</strong>
          {' '}
          Company is an independent
          contractor of the Customer in provision of the Services pursuant
          to this Agreement. No agency, partnership, joint venture, or
          employment is created as a result of this Agreement and Customer
          does not have any authority of any kind to bind Company in any
          respect whatsoever.
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        12. TERM & TERMINATION
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          <strong>Term.</strong>
          {' '}
          The term of this Agreement will commence
          on the Effective Date and, unless terminated earlier in
          accordance with this Agreement, will continue as provided in the
          Order Form, and in absence of a specific term in the Order Form,
          for one year (&quot;Initial Term&quot;). The term will
          automatically renew for successive one year terms unless either
          party notifies the other party in writing of its intent not to
          renew at least sixty (60) days prior to the end of the
          then-current Term. The &quot;Term&quot; means the Initial Term
          and all renewal terms.
        </li>
        <li className="pl-2">
          <strong>Termination.</strong>
          <ol className="list-decimal ml-6 mt-2 space-y-2">
            <li className="pl-2">
              Unless an earlier termination is permitted in an applicable
              Order Form, either party may terminate the Order Form only
              if the other party materially breaches the provisions of
              this Agreement and fails to cure the breach within fourteen
              (14) days after receiving written notice of the breach from
              the non-breaching party. For the avoidance of doubt,
              Customer&apos;s violation of section 2,3 and 6 of this
              Agreement will constitute a material breach of the
              Agreement.
            </li>
            <li className="pl-2">
              Either party may terminate this Agreement by written notice
              to the other if the other party: (i) becomes insolvent or
              admits its inability to pay its debts generally as they
              become due; (ii) becomes subject to, voluntarily or
              involuntarily, any proceeding under any bankruptcy or
              insolvency law; (iii) is dissolved or liquidated or takes
              any corporation action for such purpose; (iv) makes a
              general assignment for the benefit of creditors; or (v) has
              a receiver, trustee, or similar agent appointed by court
              order.
            </li>
          </ol>
        </li>
        <li className="pl-2">
          <strong>Effect of Termination:</strong>
          <ol className="list-decimal ml-6 mt-2 space-y-2">
            <li className="pl-2">
              Upon termination of this Agreement, all licenses granted
              herein will also terminate. Company may immediately
              terminate Customer&apos;s access to the API and Company
              Platform. Except for Customer Data, Customer will
              immediately, delete, remove, destroy or return to Company,
              any and all Platform Data.
            </li>
            <li className="pl-2">
              Upon written request by Customer following termination or
              expiration of the Agreement, Company will promptly delete or
              return all copies of Customer Data, except as explicitly
              required to be retained in accordance with applicable law.
            </li>
            <li className="pl-2">
              Within thirty (30) days following the termination or
              expiration of this Agreement, Customer will pay in full for
              the use of the Platform up to and including the last day on
              which the Platform was provided. Provided that if
              termination was due to Customer&apos;s breach of the
              Agreement or to circumstances provided in Section 12.2.2, ,
              Customer shall pay the Full license fee until the end of the
              original Subscription Term as agreed upon in the applicable
              Order Form.
            </li>
            <li className="pl-2">
              All sections of this Agreement which by their nature should
              survive termination will survive termination, including,
              without limitation, accrued rights to payment,
              confidentiality obligations, warranty disclaimers, and
              limitations of liability.
            </li>
          </ol>
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        13. MISCELLANEOUS
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          <strong>Complete Agreement.</strong>
          {' '}
          This Agreement is the
          complete and exclusive statement of the mutual understanding of
          the parties and supersedes and cancels all previous written and
          oral agreements, communications and other understandings
          relating to the subject matter of this Agreement.
        </li>
        <li className="pl-2">
          <strong>Assignment.</strong>
          {' '}
          This Agreement is not assignable,
          transferable or sublicensable by Customer except with
          Company&apos;s prior written consent. Company may transfer and
          assign any of its rights and obligations under this Agreement
          without Customer&apos;s consent. Subject to the foregoing, this
          Agreement and any Order Form shall be binding upon and inure to
          the benefit of the parties hereto and their permitted successors
          and assigns.
        </li>
        <li className="pl-2">
          <strong>Amendments.</strong>
          {' '}
          Any amendments, waivers and
          modifications to this Agreement must be in writing and signed by
          both parties, except as otherwise provided herein.
        </li>
        <li className="pl-2">
          <strong>Force Majeure.</strong>
          {' '}
          Neither Party will be liable
          for, or be considered in breach of or default under the
          Agreement on account of, any delay or failure to perform as
          required by the Agreement as a result of strike, fire,
          explosion, flood, storm, material shortages, riot, insurrection,
          governmental acts, labor conditions, acts of God, war,
          earthquake or any other cause which is beyond the reasonable
          control of such Party; provided that the non-performing Party
          gives reasonably prompt notice under the circumstances of such
          condition(s) to the other Party. The provisions of this Clause
          shall not apply to Customer&apos;s obligation to pay for
          Services rendered in accordance with the terms contained herein.
        </li>
        <li className="pl-2">
          <strong>Governing Law.</strong>
          {' '}
          This Agreement, including any
          Order Form, and any claims, whether in contract, tort or
          otherwise, arising from this Agreement or any Order Form shall
          be governed by and interpreted in accordance with the laws of
          the State of New York, without giving effect to principles of
          conflicts of law. The parties agree that the state and federal
          courts sitting in New York City, New York, USA, shall have
          proper and exclusive jurisdiction and venue for any proceedings
          arising from this Agreement.
        </li>
        <li className="pl-2">
          <strong>Notices.</strong>
          {' '}
          Any notices to be given hereunder by
          either party to the other may be affected either by personal
          delivery in writing by confirmed email, or by mail, registered
          or certified, postage prepaid with return receipt requested.
          Mailed notices shall be addressed to the parties at the
          addresses appearing in the introductory paragraph of this
          Agreement, but each party may change such address by written
          notice in accordance with this Section. Notices delivered
          personally will be deemed communicated as of actual receipt.
          Mailed notices will be deemed communicated as of fourteen (14)
          days after mailing.
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        14. PROHIBITION OF USING CUSTOMER DATA FOR LEARNING PURPOSES OF
        COMPANY&apos;S SOFTWARE
      </h2>
      <ol className="list-decimal ml-8 space-y-4">
        <li className="pl-2">
          Notwithstanding any provision of this Agreement, under no
          circumstances shall Company use the Customer Data for the
          purpose of training, fine-tuning, or otherwise enhancing any
          machine learning models, artificial intelligence tools, or
          algorithms, whether directly or indirectly. Company further
          agrees not to aggregate, anonymize, or otherwise manipulate
          Customer Data in any manner intended to support internal
          research or development activities. Use of Customer Data is
          strictly limited to fulfilling the business predictive model
          generation and data analysis services for Customer&apos;s
          exclusive use as outlined in this Agreement.
        </li>
        <li className="pl-2">
          Company shall inform the Customer about the artificial
          intelligence tools used, and provide the appropriate
          explanations and documentation to explain the functioning and
          the logic of the content and decisions generated by the
          artificial intelligence to ensure Customer&apos;s comprehension,
          and transparency. To such purpose, Company shall document and
          provide detailed information of the entire creative process
          and/or the content generation and/or decision-making process,
          documenting and providing all information regarding the prompts
          (instructions).
        </li>
        <li className="pl-2">
          The Customer shall be entitled to carry out periodic audits of
          the artificial intelligence solutions implemented by Company,
          and of Company&apos;s compliance with this Article 14, to verify
          its compliance with the terms of this Agreement and applicable
          law.
        </li>
      </ol>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        EXHIBIT A
      </h2>
      <h3 className="text-xl font-bold mt-6 mb-4 text-foreground">
        Service Level Terms
      </h3>
      <p className="mb-4">
        The Services shall be available 95%, measured monthly, excluding
        holidays and weekends and scheduled maintenance. The following
        circumstances will be excluded from any downtime calculations: (a)
        if Customer requests maintenance during these hours, any uptime or
        downtime calculation will exclude periods affected by such
        maintenance; (b) any downtime resulting from outages of
        third-party connections or utilities or other reasons beyond
        Company&apos;s control; (c) any scheduled maintenances of which
        the Company provided reasonable prior written notice to the
        Customer; (d) weekly maintenance window on Sundays of up to two
        hours between 2 – 4 AM (EST), for which no additional notice is
        required; or (e) Company&apos;s blocking of data communications or
        other Service in accordance with its policies or the terms of this
        Agreement.
      </p>
      <p className="mb-4">
        Customer&apos;s sole and exclusive remedy, and Company&apos;s
        entire liability, in connection with Service availability shall be
        that for each period of downtime lasting longer than six hours,
        Company will credit Customer 1% of Service fees for each period of
        6 or more consecutive hours of downtime; provided that no more
        than one such credit will accrue per day. Downtime shall begin to
        accrue as soon as Customer (with written notice to Company (email
        shall suffice)) recognizes that downtime is taking place, and
        continues until the availability of the Services is restored. In
        order to receive downtime credit, Customer must notify Company in
        writing within 24 hours from the time of downtime, and failure to
        provide such notice will forfeit the right to receive downtime
        credit. Such credits may not be redeemed for cash and shall not be
        cumulative beyond a total of credits for one (1) week of Service
        Fees in any one (1) calendar month in any event. Company will only
        apply a credit to the month in which the incident occurred.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4 text-foreground">
        EXHIBIT B
      </h2>
      <h3 className="text-xl font-bold mt-6 mb-4 text-foreground">
        Support Terms
      </h3>
      <p className="mb-4">
        Company will provide Technical Support to Customer via both
        telephone and electronic mail on weekdays during the hours of 9:00
        am through 6:00 pm Eastern Time, with the exclusion of official US
        public holidays (&quot;Support Hours&quot;).
      </p>
      <p className="mb-4">
        Customer may initiate a Helpdesk ticket during Support hours by
        calling their dedicated customer success manager (phone number
        provided post-contract signature), or any time by emailing
        {SUPPORT_EMAIL}
        .
      </p>
      <p className="mb-4">
        Company will use commercially reasonable efforts to respond to all
        Helpdesk tickets within one (1) business day.
      </p>
    </>
  )
}
